import { track as vercelTrack } from '@vercel/analytics';
import ReactGA from 'react-ga4';

const SESSION_KEY = 'curator_analytics_session';
const SESSION_STARTED_KEY = 'curator_analytics_session_started';
const VISITOR_KEY = 'curator_analytics_visitor';
const SHELF_VIEW_PREFIX = 'curator_shelf_view:';
const SHELF_SCROLL_PREFIX = 'curator_shelf_scroll:';
const POSTER_IMPRESSION_PREFIX = 'curator_poster_impression:';
const HERO_SLIDE_PREFIX = 'curator_hero_slide:';
const DETAIL_VIEW_PREFIX = 'curator_detail_view:';
const SEARCH_PREFIX = 'curator_search:';
const SCROLL_THRESHOLDS = [25, 50, 75, 100];

function readSessionStorage(key) {
  try {
    return sessionStorage.getItem(key);
  } catch (_err) {
    return null;
  }
}

function writeSessionStorage(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch (_err) {
    // ignore quota / privacy mode
  }
}

function readLocalStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch (_err) {
    return null;
  }
}

function writeLocalStorage(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (_err) {
    // ignore
  }
}

export function getAnalyticsSessionId() {
  let id = readSessionStorage(SESSION_KEY);
  if (!id) {
    id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    writeSessionStorage(SESSION_KEY, id);
  }
  return id;
}

export function getAnalyticsVisitorId() {
  let id = readLocalStorage(VISITOR_KEY);
  if (!id) {
    id = `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    writeLocalStorage(VISITOR_KEY, id);
  }
  return id;
}

export function isReturningVisitor() {
  return Boolean(readLocalStorage(VISITOR_KEY));
}

export function resolveAnalyticsSurface(pathname = '') {
  const path = String(pathname || '').replace(/^\/apple(?=\/|$)/, '') || '/';
  if (path === '/' || path === '') return 'home';
  if (path === '/tv' || path.startsWith('/tv/')) return 'tv-home';
  if (path.startsWith('/search')) return 'search';
  if (path.startsWith('/library')) return 'library';
  if (path.startsWith('/list/')) return 'list';
  if (path.startsWith('/account')) return 'account';
  if (/^\/(movie|tv|tv-show|person)\//.test(path)) return 'detail';
  return 'other';
}

function sanitizePayload(data = {}) {
  const session = getAnalyticsSessionId().slice(0, 32);
  const visitor = getAnalyticsVisitorId().slice(0, 32);
  const merged = { session, visitor, ...data };
  return Object.fromEntries(
    Object.entries(merged)
      .filter(([, value]) => value != null && ['string', 'number', 'boolean'].includes(typeof value))
      .map(([key, value]) => {
        const safeKey = String(key).slice(0, 64);
        if (typeof value === 'string') return [safeKey, value.slice(0, 255)];
        return [safeKey, value];
      }),
  );
}

export function trackCuratorEvent(name, data = {}) {
  const eventName = String(name || 'event').slice(0, 255);
  const payload = sanitizePayload(data);

  try {
    vercelTrack(eventName, payload);
  } catch (_err) {
    // analytics should never break the app
  }

  try {
    ReactGA.event({
      category: 'Curator',
      action: eventName,
      ...payload,
    });
  } catch (_err) {
    // ignore
  }
}

export function ensureSessionStart(path = '') {
  if (readSessionStorage(SESSION_STARTED_KEY) === '1') return;
  writeSessionStorage(SESSION_STARTED_KEY, '1');
  const returning = Boolean(readLocalStorage(VISITOR_KEY));
  getAnalyticsVisitorId();
  trackCuratorEvent('session_start', {
    path: String(path || window.location?.pathname || '/').slice(0, 255),
    surface: resolveAnalyticsSurface(path),
    returning,
    referrer: String(document?.referrer || '').slice(0, 255),
  });
}

export function trackPageView(path = '') {
  const pathname = String(path || window.location?.pathname || '/');
  trackCuratorEvent('page_view', {
    path: pathname.slice(0, 255),
    surface: resolveAnalyticsSurface(pathname),
  });
}

export function trackShelfView({ shelfKey, shelfLabel, page, itemCount }) {
  const dedupeKey = `${SHELF_VIEW_PREFIX}${page}:${shelfKey}`;
  if (readSessionStorage(dedupeKey) === '1') return;
  writeSessionStorage(dedupeKey, '1');
  trackCuratorEvent('shelf_view', {
    shelf: String(shelfKey || '').slice(0, 64),
    label: String(shelfLabel || shelfKey || '').slice(0, 120),
    page: String(page || 'home').slice(0, 32),
    items: Number(itemCount) || 0,
  });
}

export function trackShelfScrollDepth({ shelfKey, shelfLabel, page, itemCount, depthPct }) {
  const depth = Math.max(0, Math.min(100, Math.round(Number(depthPct) || 0)));
  const dedupeKey = `${SHELF_SCROLL_PREFIX}${page}:${shelfKey}`;
  let passed = [];
  try {
    passed = JSON.parse(readSessionStorage(dedupeKey) || '[]');
  } catch (_err) {
    passed = [];
  }
  if (!Array.isArray(passed)) passed = [];

  for (const threshold of SCROLL_THRESHOLDS) {
    if (depth < threshold || passed.includes(threshold)) continue;
    passed.push(threshold);
    writeSessionStorage(dedupeKey, JSON.stringify(passed));
    trackCuratorEvent('shelf_scroll', {
      shelf: String(shelfKey || '').slice(0, 64),
      label: String(shelfLabel || shelfKey || '').slice(0, 120),
      page: String(page || 'home').slice(0, 32),
      items: Number(itemCount) || 0,
      depth: threshold,
    });
  }
}

export function trackHeroSlideView({ page, slideIndex, totalSlides }) {
  const slide = Math.max(0, Math.round(Number(slideIndex) || 0));
  const dedupeKey = `${HERO_SLIDE_PREFIX}${page}:${slide}`;
  if (readSessionStorage(dedupeKey) === '1') return;
  writeSessionStorage(dedupeKey, '1');
  trackCuratorEvent('hero_slide_view', {
    page: String(page || 'home').slice(0, 32),
    slide,
    total: Number(totalSlides) || 0,
  });
}

export function trackPosterImpression({
  page, shelf, index, tmdbId, title, mediaType,
}) {
  const id = String(tmdbId || '').slice(0, 32);
  if (!id) return;
  const dedupeKey = `${POSTER_IMPRESSION_PREFIX}${page}:${shelf}:${id}`;
  if (readSessionStorage(dedupeKey) === '1') return;
  writeSessionStorage(dedupeKey, '1');
  trackCuratorEvent('poster_impression', {
    page: String(page || 'home').slice(0, 32),
    shelf: String(shelf || '').slice(0, 64),
    index: Number(index) || 0,
    tmdbId: id,
    title: String(title || '').slice(0, 120),
    media: String(mediaType || '').slice(0, 16),
  });
}

export function trackPosterClick({
  page, shelf, index, tmdbId, title, mediaType,
}) {
  trackCuratorEvent('poster_click', {
    page: String(page || 'home').slice(0, 32),
    shelf: String(shelf || '').slice(0, 64),
    index: Number(index) || 0,
    tmdbId: String(tmdbId || '').slice(0, 32),
    title: String(title || '').slice(0, 120),
    media: String(mediaType || '').slice(0, 16),
  });
}

export function trackRailLinkClick({ page, shelfKey, shelfLabel }) {
  trackCuratorEvent('rail_link_click', {
    page: String(page || 'home').slice(0, 32),
    shelf: String(shelfKey || '').slice(0, 64),
    label: String(shelfLabel || shelfKey || '').slice(0, 120),
  });
}

export function trackDetailView({ mediaKind, tmdbId, title }) {
  const id = String(tmdbId || '').slice(0, 32);
  if (!id) return;
  const dedupeKey = `${DETAIL_VIEW_PREFIX}${mediaKind}:${id}`;
  if (readSessionStorage(dedupeKey) === '1') return;
  writeSessionStorage(dedupeKey, '1');
  trackCuratorEvent('detail_view', {
    media: String(mediaKind || '').slice(0, 16),
    tmdbId: id,
    title: String(title || '').slice(0, 120),
  });
}

export function trackSearchQuery({ query, movies = 0, tv = 0, people = 0 }) {
  const term = String(query || '').trim().slice(0, 120);
  if (!term) return;
  const dedupeKey = `${SEARCH_PREFIX}${term.toLowerCase()}`;
  if (readSessionStorage(dedupeKey) === '1') return;
  writeSessionStorage(dedupeKey, '1');
  trackCuratorEvent('search_query', {
    query: term,
    movies: Number(movies) || 0,
    tv: Number(tv) || 0,
    people: Number(people) || 0,
    total: (Number(movies) || 0) + (Number(tv) || 0) + (Number(people) || 0),
  });
}

export function trackListSort({ listKey, sortField, sortOrder }) {
  trackCuratorEvent('list_sort', {
    list: String(listKey || '').slice(0, 64),
    field: String(sortField || '').slice(0, 32),
    order: String(sortOrder || '').slice(0, 8),
  });
}

export function computeHorizontalScrollDepth(el) {
  if (!el) return 0;
  const maxScroll = Math.max(el.scrollWidth - el.clientWidth, 0);
  if (maxScroll <= 0) return 100;
  return Math.round((el.scrollLeft / maxScroll) * 100);
}
